import { useCallback, useEffect, useState } from 'react';
import Table, { Order } from '../../../components/Table';
import { IPaginationData } from '../../../api/base';
import moment from 'moment';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import theme from '../../../theme';
import mentorApi from '../../../api/mentor.api';
import { LogoImage } from '../../styled';
import ImageUploadWithPreview from '../../../components/ImageUpload';
import { EMentorType, IMentor } from '../../../models/mentor.model';
import _ from 'lodash';
import MentorTypeSelect from '../../../components/MentorTypeSelect';
import { isAxiosError } from 'axios';
import { extractResErrorMessage } from '../../../utils/error';

export default function MentorListPage() {
  const [type, setType] = useState(EMentorType.Touchstone);
  const [dataSource, setDataSource] = useState<IPaginationData<IMentor>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });
  const [loading, setLoading] = useState(false);
  const [[orderBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >(['priority', 'desc']);

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [error, setError] = useState<Record<string, string>>({});

  const [currItem, setCurrItem] = useState<Partial<IMentor> | undefined>();
  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    mentorApi
      .getList({
        ...filter,
        type,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: orderBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [order, orderBy, filter, type]);

  useEffect(loadData, [loadData]);

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">Mentor</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <div>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => {
                setError({});
                setCurrItem({ type });
              }}
            >
              Create new
            </Button>
          </div>
        </div>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          variant="fullWidth"
          value={type}
          onChange={(ev, v) => setType(v)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 700,
              fontSize: 18,
            },
          }}
        >
          {Object.values(EMentorType).map((t) => (
            <Tab key={t} label={_.capitalize(_.words(t).join(' '))} value={t} />
          ))}
        </Tabs>
      </Box>
      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={orderBy}
        loading={loading}
        // onClick={setCurrItem}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'priority',
            title: 'Index',
            sortable: true,
            dataKey: 'priority',
            render: (row) => row.priority || 0,
          },
          {
            key: 'photo_url',
            title: 'Photo',
            dataKey: 'photo_url',
            render: (data) =>
              data.photo_url ? (
                <LogoImage
                  src={process.env.REACT_APP_API_URI + data.photo_url}
                />
              ) : null,
          },
          {
            key: 'name',
            title: 'Name',
            dataKey: 'name',
            sortable: true,
            render: (data) => (
              <>
                <div className="one-lines">{data.name_vi}</div>
                <div className="one-lines">{data.name_en}</div>
              </>
            ),
          },
          {
            key: 'title',
            title: 'Title',
            dataKey: 'title',
            sortable: false,
            render: (data) => (
              <>
                <div className="one-lines">{data.title_vi}</div>
                <div className="one-lines">{data.title_en}</div>
              </>
            ),
          },
          {
            key: 'company',
            title: 'Company',
            dataKey: 'company',
            sortable: false,
            render: (data) => (
              <>
                <div className="one-lines">{data.company_vi}</div>
                <div className="one-lines">{data.company_en}</div>
              </>
            ),
          },

          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            sortable: true,
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            sortable: true,
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'actions',
            title: 'Actions',
            dataKey: 'id',
            render: (data) => (
              <>
                <IconButton
                  onClick={() => {
                    setError({});
                    setCurrItem(data);
                  }}
                >
                  <Edit />
                </IconButton>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />

      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this mentor?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                mentorApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete submission successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!currItem} onClose={() => setCurrDeletedId(undefined)}>
        <DialogTitle>
          {currItem?.id ? 'Update mentor' : 'Create new mentor'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" p={1}>
                Photo
              </Typography>
              <ImageUploadWithPreview
                style={{ width: 256 }}
                value={currItem?.photo_url}
                error={error?.photo_url}
                onChange={(value) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      photo_url: value,
                    });
                  }
                }}
              />
            </Grid>
            {currItem?.type ? (
              <Grid item xs={12}>
                <MentorTypeSelect
                  margin="normal"
                  value={currItem.type}
                  onChange={(value) => {
                    if (currItem) {
                      setCurrItem({
                        ...currItem,
                        type: value,
                      });
                    }
                  }}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <TextField
                label="Index"
                size="small"
                error={!!error?.priority}
                helperText={error?.priority}
                fullWidth
                type="number"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.priority || 0}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      priority: +e.currentTarget.value || 0,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" p={1}>
                Name
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="English"
                size="small"
                error={!!error?.name_en}
                helperText={error?.name_en}
                fullWidth
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.name_en}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      name_en: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Vietnamese"
                size="small"
                type="text"
                fullWidth
                error={!!error?.name_vi}
                helperText={error?.name_vi}
                inputProps={{
                  min: 1,
                }}
                value={currItem?.name_vi}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      name_vi: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" p={1}>
                Title
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="English"
                size="small"
                fullWidth
                error={!!error?.title_en}
                helperText={error?.title_en}
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.title_en}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      title_en: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Vietnamese"
                size="small"
                fullWidth
                error={!!error?.title_vi}
                helperText={error?.title_vi}
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.title_vi}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      title_vi: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" p={1}>
                Company
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="English"
                size="small"
                fullWidth
                error={!!error?.company_en}
                helperText={error?.company_en}
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.company_en}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      company_en: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Vietnamese"
                size="small"
                fullWidth
                error={!!error?.company_vi}
                helperText={error?.company_vi}
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.company_vi}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      company_vi: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" p={1}>
                Link
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                error={!!error?.link}
                helperText={error?.link}
                fullWidth
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.link}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      link: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
          </Grid>
          {/*         
          <TextField
            label="Index"
            size="small"
            margin="normal"
            type="number"
            inputProps={{
              min: 1,
            }}
            value={currItem?.priority ?? 1}
            onChange={(e) => {
              if (currItem) {
                setCurrItem({
                  ...currItem,
                  priority: +(e.currentTarget.value || 1),
                });
              }
            }}
          /> */}
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrItem(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            variant="contained"
            onClick={() => {
              if (!currItem) {
                return;
              }
              Promise.resolve()
                .then(() => {
                  setDeleting(true);
                  setError({});
                })
                .then(() => {
                  if (currItem?.id) {
                    return mentorApi
                      .update(currItem.id, currItem)
                      .then(() => {
                        toast.success('Update successful');
                      })
                      .catch((e) => {
                        if (isAxiosError(e)) {
                          const message = e.response?.data?.message;
                          const error = extractResErrorMessage(message);
                          if (typeof error === 'object') {
                            setError(error);
                            throw e;
                          }
                        }
                        toast.error(e.message ?? e.toString());
                        throw e;
                      });
                  }
                  return mentorApi
                    .create(currItem)
                    .then(() => {
                      toast.success('Update successful');
                    })
                    .catch((e) => {
                      if (isAxiosError(e)) {
                        const message = e.response?.data?.message;
                        const error = extractResErrorMessage(message);
                        if (typeof error === 'object') {
                          setError(error);
                          throw e;
                        }
                      }
                      toast.error(e.message ?? e.toString());
                      throw e;
                    });
                })
                .then(() => {
                  loadData();
                  setCurrItem(undefined);
                })
                .finally(() => {
                  setDeleting(false);
                });
            }}
            color="primary"
          >
            {deleting ? (
              <CircularProgress size={16} />
            ) : currItem?.id ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
